// src/modules/customers/components/IfscInput.jsx
//
// IFSC field with a bank/branch lookup. Looks the code up as soon as a full,
// well-formed IFSC has been typed (or on "Look up"), shows what it resolved
// to, and hands the result to `onResolved` so the caller can fill its own
// bank/branch fields. Drop it straight into a Form.Item — it takes the
// injected value/onChange.

import React, { useEffect, useRef, useState } from 'react';
import { Button, Input } from 'antd';
import { SearchOutlined, CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import { lookupIfsc } from '../api';

export const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;

/** "SBI - MG ROAD" — the same shape bankFieldsFrom() writes into bankBranchName. */
export const bankBranchLabel = (info) => [info?.bank, info?.branch].filter(Boolean).join(' - ');

export default function IfscInput({ value, onChange, onResolved, disabled, size, placeholder = 'e.g. SBIN0001234' }) {
  const [state, setState] = useState({ status: 'idle' }); // idle | loading | found | error
  // The code a lookup was last started for — answers for any other code are stale.
  const pending = useRef(null);

  // Whatever we showed belongs to a code that is no longer in the field
  // (cleared, edited, or swapped by a saved-account pick).
  useEffect(() => {
    if (state.code && state.code !== value) setState({ status: 'idle' });
  }, [value, state.code]);

  const lookup = async (raw = value) => {
    const code = String(raw || '').trim().toUpperCase();
    if (!IFSC_PATTERN.test(code)) {
      if (code) setState({ status: 'error', code, message: 'Enter a valid 11-character IFSC, e.g. SBIN0001234' });
      return;
    }
    pending.current = code;
    setState({ status: 'loading', code });
    try {
      const info = await lookupIfsc(code);
      if (pending.current !== code) return;
      setState({ status: 'found', code, info });
      onResolved?.(info);
    } catch (err) {
      if (pending.current !== code) return;
      setState({
        status: 'error',
        code,
        message: err?.response?.data?.message || 'IFSC lookup failed — enter the bank and branch by hand',
      });
    }
  };

  const handleChange = (e) => {
    const next = e.target.value.toUpperCase().replace(/\s/g, '');
    onChange?.(next);
    // Finishing the 11th character is the natural moment to look it up.
    if (next.length === 11 && next !== value) lookup(next);
  };

  const { info } = state;
  return (
    <>
      <Input
        size={size}
        value={value}
        onChange={handleChange}
        onPressEnter={(e) => { e.preventDefault(); lookup(); }}
        maxLength={11}
        placeholder={placeholder}
        disabled={disabled}
        style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', letterSpacing: '0.04em' }}
        suffix={(
          <Button
            type="link"
            size="small"
            icon={<SearchOutlined />}
            loading={state.status === 'loading'}
            disabled={disabled || !value}
            onClick={() => lookup()}
            style={{ marginRight: -6 }}
          >
            Look up
          </Button>
        )}
      />
      {state.status === 'found' && info && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#237804', lineHeight: 1.45 }}>
          <CheckCircleFilled style={{ marginRight: 6 }} />
          <strong>{info.bank}</strong>
          {info.branch && <> · {info.branch}</>}
          {(info.city || info.state) && (
            <span style={{ color: '#64748b' }}> · {[info.city, info.state].filter(Boolean).join(', ')}</span>
          )}
        </div>
      )}
      {state.status === 'error' && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#ad6800' }}>
          <ExclamationCircleFilled style={{ marginRight: 6 }} />
          {state.message}
        </div>
      )}
    </>
  );
}
